import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sunrise, Sunset, Radio, Minus, Plus, Flame } from "lucide-react";
import { api, inr } from "@/lib/api";

let cache = null;
export function useLiveSlots() {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cache || Date.now() - cache.at > 5000) {
      cache = { at: Date.now(), p: api.get("/live-slots", { params: { days: 7 } })
        .then((r) => ({ dates: r.data.dates, safaris: r.data.safaris.filter((s) => s.slots.length) }))
        .catch(() => ({ dates: [], safaris: [] })) };
    }
    let live = true;
    cache.p.then((d) => live && setData(d));
    return () => { live = false; };
  }, []);
  return data;
}

export const checkoutUrl = (safariId, slot, guests = 2) => `/checkout?${new URLSearchParams({
  safari_id: safariId, slot_id: slot.id, booking_type: "private",
  vehicles: String(Math.ceil(guests / slot.seats_per_vehicle)), adults: String(guests), children: "0" })}`;

export const fmtDay = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  return { wd: dt.toLocaleDateString("en-IN", { weekday: "short" }), day: dt.getDate(),
    mon: dt.toLocaleDateString("en-IN", { month: "short" }), dow: dt.getDay() };
};

const sellingFast = (slot) => [5, 6, 0].includes(fmtDay(slot.date).dow) && slot.remaining_vehicles > 0 && slot.remaining_vehicles <= 2;

function GuestPicker({ id, guests, setGuests, max }) {
  const btn = "w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 disabled:opacity-30";
  return (
    <div className="flex items-center gap-2 mt-2 text-xs" data-testid={`live-slot-guests-${id}`}>
      <button className={btn} disabled={guests <= 1} onClick={() => setGuests(guests - 1)} data-testid={`live-slot-guests-minus-${id}`} aria-label="Fewer guests"><Minus className="w-3 h-3"/></button>
      <span className="min-w-[52px] text-center" data-testid={`live-slot-guests-val-${id}`}>{guests} guest{guests > 1 ? "s" : ""}</span>
      <button className={btn} disabled={guests >= max} onClick={() => setGuests(guests + 1)} data-testid={`live-slot-guests-plus-${id}`} aria-label="More guests"><Plus className="w-3 h-3"/></button>
    </div>
  );
}

function SlotCell({ slot, session, safariId }) {
  const nav = useNavigate();
  const [guests, setGuests] = useState(2);
  const Icon = session === "morning" ? Sunrise : Sunset;
  const label = session === "morning" ? "Morning" : "Evening";
  if (!slot) return (
    <div className="rounded-xl border border-dashed border-white/10 p-3 text-xs text-white/35 min-h-[168px] flex flex-col" data-testid={`live-slot-none-${session}`}>
      <span className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5"/>{label}</span>
      <span className="mt-auto">No safari</span>
    </div>
  );
  const left = slot.remaining_vehicles;
  const status = slot.started ? "Started" : left === 0 ? "Sold out" : null;
  const jeeps = Math.ceil(guests / slot.seats_per_vehicle);
  const hot = sellingFast(slot);
  return (
    <div className={`relative rounded-xl p-3 min-h-[168px] flex flex-col transition-colors duration-200 ${status ? "bg-white/[0.03] text-white/40" : "bg-white/[0.07] hover:bg-white/[0.12] text-[#F5F0EB]"} ${hot ? "ring-1 ring-[#E8A15A]/60" : ""}`}
      data-testid={`live-slot-${slot.id}`}>
      {hot && <span className="absolute -top-2 right-2 flex items-center gap-1 bg-[#C86D51] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`live-slot-selling-fast-${slot.id}`}><Flame className="w-3 h-3"/>Selling fast</span>}
            <span className="flex items-center gap-1.5 text-xs whitespace-nowrap"><Icon className="w-3.5 h-3.5 text-[#D4A359] shrink-0"/>{label} · {slot.departure_time}</span>
      {status ? <span className="mt-auto text-sm font-medium" data-testid={`live-slot-status-${slot.id}`}>{status}</span> : (
        <>
          <span className={`mt-2 text-sm font-semibold ${left <= 2 ? "text-[#E8A15A]" : ""}`} data-testid={`live-slot-left-${slot.id}`}>
            {left} {left === 1 ? "jeep" : "jeeps"} left
          </span>
          <span className="text-xs text-white/55">{inr(slot.price)} / jeep</span>
          <GuestPicker id={slot.id} guests={guests} setGuests={setGuests} max={left * slot.seats_per_vehicle}/>
          <span className="text-[11px] text-white/55 mt-1" data-testid={`live-slot-jeeps-${slot.id}`}>{jeeps} jeep{jeeps > 1 ? "s" : ""} · {inr(slot.price * jeeps)}</span>
          <button onClick={() => nav(checkoutUrl(safariId, slot, guests))} data-testid={`live-slot-book-${slot.id}`}
            className="mt-auto pt-2 text-left text-xs font-semibold uppercase tracking-wider text-[#D4A359] hover:text-white transition-colors duration-200">
            Book →
          </button>
        </>
      )}
    </div>
  );
}

export function LiveSlots() {
  const data = useLiveSlots();
  const [active, setActive] = useState("");
  if (!data || !data.safaris.length) return null;
  const safari = data.safaris.find((s) => s.id === active) || data.safaris[0];
  const pick = (d, ses) => safari.slots.find((s) => s.date === d && s.session === ses);

  return (
    <section className="bg-[#121A16] text-[#F5F0EB] py-14 md:py-16" data-testid="live-slots-section">
      <div className="jt-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
          <div>
            <div className="jt-eyebrow text-[#D4A359] mb-3 flex items-center gap-2">
              <Radio className="w-3.5 h-3.5 animate-pulse"/> Live availability
            </div>
            <h2 className="font-display text-3xl md:text-4xl" data-testid="live-slots-title">Safari slots · next 7 days</h2>
            <p className="text-sm text-white/60 mt-2">Private jeeps, up to 6 guests each. Set your group size on a slot and book it.</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="live-slots-tabs">
            {data.safaris.map((s) => (
              <button key={s.id} onClick={() => setActive(s.id)} data-testid={`live-slots-tab-${s.slug}`}
                className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 ${s.id === safari.id ? "bg-[#D4A359] text-[#121A16] font-semibold" : "bg-white/[0.07] hover:bg-white/[0.14]"}`}>
                {s.name.replace(/^Jawai\s+/, "")}
              </button>
            ))}
          </div>
        </div>
        <div className="-mx-4 px-4 overflow-x-auto pt-2 pb-2">
          <div className="grid grid-cols-7 gap-3 min-w-[900px]" data-testid="live-slots-grid">
            {data.dates.map((d, i) => {
              const f = fmtDay(d);
              return (
                <div key={d} className="space-y-3" data-testid={`live-slots-day-${d}`}>
                  <div className="pb-1">
                    <div className="text-xs uppercase tracking-wider text-white/50">{i === 0 ? "Today" : i === 1 ? "Tomorrow" : f.wd}</div>
                    <div className="font-display text-xl">{f.day} <span className="text-sm text-white/60">{f.mon}</span></div>
                  </div>
                  <SlotCell key={`${safari.id}-m`} slot={pick(d, "morning")} session="morning" safariId={safari.id}/>
                  <SlotCell key={`${safari.id}-e`} slot={pick(d, "evening")} session="evening" safariId={safari.id}/>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
